/**
 * The resume endpoint returns `skill` and `additional_info` as arrays of STRINGS
 * containing Python-style pseudo-JSON (single-quoted). These helpers turn that into
 * usable objects.
 *
 * Lifted verbatim from the original CandidateDetails page so both UIs read the payload
 * identically — if this parsing is wrong, it should be wrong (and fixed) in one place.
 * See docs/backend-spec-console.md: the real fix is for the API to return JSON.
 */

export const parseJsonString = (jsonString) => {
  if (!jsonString) return null;
  try {
    if (
      typeof jsonString === "string" &&
      (jsonString.trim().startsWith("{") || jsonString.trim().startsWith("["))
    ) {
      // Python dict repr uses single quotes, which JSON.parse rejects.
      return JSON.parse(jsonString.replace(/'/g, '"'));
    }
    return jsonString;
  } catch (_) {
    // Apostrophes inside values ("Bachelor's") break the naive quote swap. Return the
    // raw string rather than throwing — a section renders empty instead of the page dying.
    return jsonString;
  }
};

export const extractProjects = (additionalInfo) => {
  if (!Array.isArray(additionalInfo)) return [];
  const projects = [];
  additionalInfo.forEach((info) => {
    const parsed = parseJsonString(info);
    if (parsed && typeof parsed === "object" && Array.isArray(parsed.Projects)) {
      projects.push(...parsed.Projects);
    }
  });
  return projects;
};

export const extractAchievements = (additionalInfo) => {
  if (!Array.isArray(additionalInfo)) return [];
  let achievements = [];
  additionalInfo.forEach((info) => {
    const parsed = parseJsonString(info);
    if (parsed && typeof parsed === "object" && Array.isArray(parsed.Achievements)) {
      achievements = parsed.Achievements;
    }
  });
  return achievements;
};

/** Skills arrive either flat ({Languages: [...]}) or nested one level deep. */
export const extractSkills = (skillArray) => {
  if (!Array.isArray(skillArray)) return [];
  const categories = [];

  skillArray.forEach((skillItem) => {
    const parsed = parseJsonString(skillItem);
    if (!parsed || typeof parsed !== "object") return;

    Object.entries(parsed).forEach(([category, skills]) => {
      if (Array.isArray(skills)) {
        categories.push({ category, skills });
      } else if (skills && typeof skills === "object") {
        Object.entries(skills).forEach(([subCategory, subSkills]) => {
          if (Array.isArray(subSkills)) {
            categories.push({ category: `${category} — ${subCategory}`, skills: subSkills });
          }
        });
      }
    });
  });

  return categories;
};
